import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export const APPLICATION_TOOLTIP_VIEWPORT_MARGIN = 12;
export const APPLICATION_TOOLTIP_VERTICAL_OFFSET = 8;

function chainHandlers(...handlers) {
  return (event) => {
    handlers.forEach((handler) => {
      if (typeof handler === "function") {
        handler(event);
      }
    });
  };
}

export function computeApplicationTooltipPosition({
  triggerRect,
  tooltipRect,
  viewportWidth,
  viewportHeight,
  margin = APPLICATION_TOOLTIP_VIEWPORT_MARGIN,
  offset = APPLICATION_TOOLTIP_VERTICAL_OFFSET,
}) {
  const safeViewportWidth = Math.max(viewportWidth, margin * 2);
  const safeViewportHeight = Math.max(viewportHeight, margin * 2);
  const tooltipWidth = Math.min(
    tooltipRect.width || 0,
    safeViewportWidth - margin * 2
  );
  const tooltipHeight = Math.min(
    tooltipRect.height || 0,
    safeViewportHeight - margin * 2
  );

  const centeredLeft =
    (triggerRect.left || 0) +
    ((triggerRect.width || 0) - tooltipWidth) / 2;

  const left = Math.max(
    margin,
    Math.min(
      centeredLeft,
      safeViewportWidth - tooltipWidth - margin
    )
  );

  const aboveTop = (triggerRect.top || 0) - offset - tooltipHeight;
  const belowTop = (triggerRect.bottom || 0) + offset;
  const fitsAbove = aboveTop >= margin;
  const fitsBelow =
    belowTop + tooltipHeight <= safeViewportHeight - margin;

  let top = aboveTop;
  let placement = "above";

  if (!fitsAbove && fitsBelow) {
    top = belowTop;
    placement = "below";
  } else if (!fitsAbove && !fitsBelow) {
    top = Math.max(
      margin,
      Math.min(
        belowTop,
        safeViewportHeight - tooltipHeight - margin
      )
    );
    placement = "below";
  }

  return {
    left,
    top,
    placement,
    maxWidth: safeViewportWidth - margin * 2,
  };
}

export default function ApplicationTooltip({
  children,
  content,
}) {
  const [open, setOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState(null);
  const [placement, setPlacement] = useState("above");
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTooltipStyle(null);
      setPlacement("above");
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) {
      return undefined;
    }

    const updatePosition = () => {
      if (!triggerRef.current || !tooltipRef.current) {
        return;
      }

      const nextPosition = computeApplicationTooltipPosition({
        triggerRect:
          triggerRef.current.getBoundingClientRect(),
        tooltipRect:
          tooltipRef.current.getBoundingClientRect(),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });

      setPlacement(nextPosition.placement);
      setTooltipStyle({
        left: `${nextPosition.left}px`,
        top: `${nextPosition.top}px`,
        maxWidth: `${nextPosition.maxWidth}px`,
      });
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  if (!isValidElement(children)) {
    return children ?? null;
  }

  const childProps = children.props || {};

  const trigger = cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: chainHandlers(
      childProps.onMouseEnter,
      () => setOpen(true)
    ),
    onMouseLeave: chainHandlers(
      childProps.onMouseLeave,
      () => setOpen(false)
    ),
    onFocus: chainHandlers(
      childProps.onFocus,
      () => setOpen(true)
    ),
    onBlur: chainHandlers(
      childProps.onBlur,
      () => setOpen(false)
    ),
  });

  return (
    <>
      {trigger}
      {open
        ? createPortal(
            <span
              aria-hidden={open ? "false" : "true"}
              className={[
                "av-app-tooltip__bubble",
                `av-app-tooltip__bubble--${placement}`,
                open ? "is-visible" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              id={tooltipId}
              ref={tooltipRef}
              role="tooltip"
              style={tooltipStyle || undefined}
            >
              {content}
            </span>,
            document.body
          )
        : null}
    </>
  );
}
